import { SDK_KEY_HEADER } from './constants';
import type {
  SdkConfigResponse,
  RecordConsentsBody,
  ConsentsResponse,
} from './types';

export class VeridionApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'VeridionApiError';
  }
}

export interface HttpClientConfig {
  apiUrl: string;
  publicKey: string;
}

export class HttpClient {
  private readonly apiUrl: string;
  private readonly publicKey: string;

  constructor(config: HttpClientConfig) {
    this.apiUrl = config.apiUrl.replace(/\/$/, '');
    this.publicKey = config.publicKey;
  }

  async getConfig(subjectId?: string): Promise<SdkConfigResponse> {
    const params = subjectId
      ? `?subjectId=${encodeURIComponent(subjectId)}`
      : '';
    return this.request<SdkConfigResponse>('GET', `/v1/sdk/config${params}`);
  }

  async recordConsents(body: RecordConsentsBody): Promise<ConsentsResponse> {
    return this.request<ConsentsResponse>('POST', '/v1/sdk/consents', body);
  }

  async getConsentStatus(subjectId: string): Promise<ConsentsResponse> {
    return this.request<ConsentsResponse>(
      'GET',
      `/v1/sdk/consents/status?subjectId=${encodeURIComponent(subjectId)}`,
    );
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.apiUrl}${path}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          [SDK_KEY_HEADER]: this.publicKey,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      throw new VeridionApiError(
        0,
        `Network error: ${err instanceof Error ? err.message : String(err)}`,
      );
    }

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new VeridionApiError(
        response.status,
        (payload as Record<string, string>).error || 'Request failed',
      );
    }

    return response.json() as Promise<T>;
  }
}
